// Copyright 2023 Anthony Howell. All rights reserved.

import { Card, GameType, GameTypeExtensions } from './types';

class GameLogic {
    deck: Card[];
    playersCards: Card[][];
    boardCards: Card[];
    selectedGameType: GameType;
  
    constructor(deck: Card[], playersCards: Card[][], boardCards: Card[], selectedGameType: GameType) {
      this.deck = deck;
      this.playersCards = playersCards;
      this.boardCards = boardCards;
      this.selectedGameType = selectedGameType;
    }
  
    determineWinnersAndTies(): [number[][], number[][], number[], number[]] {
      if (this.boardCards.length === 0) {
        return [[], [], [], []];
      }
    
      const highPlayerStrengths: number[] = [];
      const lowPlayerStrengths: number[] = [];
      let highResults: number[][] = [];
      let lowResults: number[][] = [];
    
      for (const playerHand of this.playersCards) {
        const playerCombinations = this.returnAllCombinations(playerHand, this.boardCards);
        highPlayerStrengths.push(this.highestRelativeHandStrength(playerCombinations));
    
        if (GameTypeExtensions[this.selectedGameType].isLowGame) {
          lowPlayerStrengths.push(this.lowestRelativeHandStrength(playerCombinations));
        }
      }
    
      const highestHighStrength = Math.max(...highPlayerStrengths);
      const countHighWinners = highPlayerStrengths.filter((strength) => strength === highestHighStrength).length;
    
      highResults = highPlayerStrengths.map((strength) =>
        strength === highestHighStrength ? (countHighWinners > 1 ? [0, 1] : [1, 0]) : [0, 0]
      );
    
      if (GameTypeExtensions[this.selectedGameType].isLowGame) {
        const highestLowStrength = Math.min(...lowPlayerStrengths.filter((strength) => strength !== 10000000));
        const countLowWinners = lowPlayerStrengths.filter((strength) => strength === highestLowStrength).length;
    
        lowResults = lowPlayerStrengths.map((strength) =>
          strength !== 10000000 && strength === highestLowStrength ? (countLowWinners > 1 ? [0, 1] : [1, 0]) : [0, 0]
        );
      }
    
      return [highResults, lowResults, highPlayerStrengths, lowPlayerStrengths];
    }
  
    highHandStrength(hand: Card[]): number {
      if (hand.length !== 5) {
        return 0;
      }
  
      const sortedHand = hand.sort((a, b) => a.rank - b.rank);
  
      const isFlush = new Set(hand.map((card) => card.suit)).size === 1;
      const isStraightHand = this.isStraight(sortedHand);
  
      if (isStraightHand && isFlush) {
        if (sortedHand[0].rank === 10 && sortedHand[4].rank === 14) {
          return 10000000;
        }
        return JSON.stringify(sortedHand.map((card) => card.rank)) === JSON.stringify([2, 3, 4, 5, 14]) ? 9000000 + 5 : 9000000 + sortedHand[4].rank;
      }
  
      const rankCounts = hand.reduce((counts: { [rank: string]: number }, card) => {
        counts[card.rank] = (counts[card.rank] || 0) + 1;
        return counts;
      }, {});
      const sortedRankCounts = Object.values(rankCounts).sort((a, b) => b - a);
  
      switch (true) {
        case sortedRankCounts[0] === 4:
          const fourOfAKindRank = Object.keys(rankCounts).find((rank) => rankCounts[rank] === 4) || '0';
          const kickerRank = Object.keys(rankCounts).find((rank) => rankCounts[rank] === 1) || '0';
          return 8000000 + Number(fourOfAKindRank) * 100 + Number(kickerRank);
        case sortedRankCounts[0] === 3 && sortedRankCounts[1] === 2:
          const threeOfAKindRank = Object.keys(rankCounts).find((rank) => rankCounts[rank] === 3) || '0';
          const pairRank = Object.keys(rankCounts).find((rank) => rankCounts[rank] === 2) || '0';
          return 7000000 + Number(threeOfAKindRank) * 10 + Number(pairRank);
        case isFlush:
          return (
            6000000 +
            sortedHand
              .reverse()
              .reduce((score, card, index) => score + card.rank * Math.pow(10, 4 - index), 0)
          );
        case isStraightHand:
          return JSON.stringify(sortedHand.map((card) => card.rank)) === JSON.stringify([2, 3, 4, 5, 14]) ? 5000000 + 5 : 5000000 + sortedHand[4].rank;
        case sortedRankCounts[0] === 3 && sortedRankCounts[1] === 1:
          const threeOfAKindRankOnly = Object.keys(rankCounts).find((rank) => rankCounts[rank] === 3) || '0';
          const kickers = sortedHand.filter((card) => card.rank !== Number(threeOfAKindRankOnly)).map((card) => card.rank).sort((a, b) => b - a);
          const kickerScore = kickers[0] * 100 + kickers[1];
          return 4000000 + Number(threeOfAKindRankOnly) * 100 + kickerScore;
        case sortedRankCounts[0] === 2 && sortedRankCounts[1] === 2:
          const pairs = Object.keys(rankCounts)
            .filter((rank) => rankCounts[rank] === 2)
            .map(Number)
            .sort((a, b) => b - a);
          const kicker = sortedHand.find((card) => card.rank !== pairs[0] && card.rank !== pairs[1])?.rank || 0;
          return 3000000 + pairs[0] * 1000 + pairs[1] * 70 + kicker;
        case sortedRankCounts.includes(2):
        const pairRankInTwoPair = Math.max(...Object.keys(rankCounts).filter((rank) => rankCounts[rank] === 2).map(Number));
        const kickersInTwoPair = sortedHand
          .filter((card) => card.rank !== pairRankInTwoPair)
          .map((card) => card.rank)
          .sort((a, b) => b - a);
        const kickerScoreInTwoPair = kickersInTwoPair.reduce((score, rank, index) => score + rank * [700, 50, 1][index], 0);
        return 2000000 + pairRankInTwoPair * 1000 + kickerScoreInTwoPair;
        default:
          return (
            1000000 +
            sortedHand.reduce((score, card, index) => score + card.rank * [40000, 2800, 200, 14, 1][index], 0)
          );
      }
    }
  
    lowHandStrength(hand: Card[]): number {
      const adjustedHand = hand.map((card) => (card.rank === 14 ? { ...card, rank: 1 } : card));
      const validLowCards = adjustedHand.filter((card) => card.rank <= 8).sort((a, b) => a.rank - b.rank);
      const uniqueRanks = new Set(validLowCards.map((card) => card.rank));
  
      if (uniqueRanks.size !== 5) {
        return 10000000;
      }
  
      return validLowCards.reverse().reduce((score, card, index) => score + card.rank * Math.pow(10, index), 0);
    }
  
    highestRelativeHandStrength(combinations: Card[][]): number {
      return Math.max(...combinations.map((combination) => this.highHandStrength(combination)));
    }
  
    lowestRelativeHandStrength(combinations: Card[][]): number {
      return Math.min(...combinations.map((combination) => this.lowHandStrength(combination)));
    }
  
    isStraight(hand: Card[]): boolean {
      const ranks = hand.map((card) => card.rank);
      return ranks.every((rank, index) => index === 0 || ranks[index - 1] === rank - 1) || JSON.stringify(ranks) === JSON.stringify([2, 3, 4, 5, 14]);
    }
  
    returnAllCombinations(hand: Card[], board: Card[]): Card[][] {
      const selectedGameType = this.selectedGameType;
      const combinations: Card[][] = [];
  
      if (hand.length !== GameTypeExtensions[selectedGameType].maxPlayersCards || board.length !== 5) {
        return combinations;
      }
  
      const allCards = [...hand, ...board];
  
      switch (selectedGameType) {
        case GameType.Holdem:
          if (hand.length === 2) {
            for (let i = 0; i < allCards.length; i++) {
              for (let j = i + 1; j < allCards.length; j++) {
                for (let k = j + 1; k < allCards.length; k++) {
                  for (let l = k + 1; l < allCards.length; l++) {
                    for (let m = l + 1; m < allCards.length; m++) {
                      const combination = [allCards[i], allCards[j], allCards[k], allCards[l], allCards[m]];
                      combinations.push(combination);
                    }
                  }
                }
              }
            }
          }
          break;
        case GameType.Plo:
        case GameType.Plo8:
        case GameType.FivePLO:
        case GameType.FivePLO8:
        case GameType.SixPLO:
        case GameType.SixPLO8:
          for (let i = 0; i < hand.length; i++) {
            for (let j = i + 1; j < hand.length; j++) {
              const heroHandPart = [hand[i], hand[j]];
  
              for (let k = 0; k < board.length; k++) {
                for (let l = k + 1; l < board.length; l++) {
                  for (let m = l + 1; m < board.length; m++) {
                    const boardCombination = [board[k], board[l], board[m]];
                    const combination = [...heroHandPart, ...boardCombination];
                    combinations.push(combination);
                  }
                }
              }
            }
          }
          break;
        default:
          break;
      }
  
      return combinations;
    }
  }

  export { GameLogic };
